import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { apiResponse } from 'src/app/models/apiResponse.model';
import { OrderByConfig, ApiFilter, FieldsSelection, DefaultFilter, GroupByConfig } from 'src/app/models/apiParams.model';
import { environment } from '../../../../environments/environment';

/**
 * Base API service with shared functionality for all domain-specific API services.
 * Provides common HTTP methods, parameter building, and header management.
 */
@Injectable({ providedIn: 'root' })
export abstract class BaseApiService {
    protected api_base: string = environment.apiUrl;
    protected defaultHeaders = new HttpHeaders().set('Accept', 'application/json');

    constructor(protected http: HttpClient) { }

    /**
     * Builds HttpParams from optional query parameters
     */
    protected buildParams(options: {
        limit?: number;
        page?: number;
        orderBy?: string | OrderByConfig[];
        filter?: string | ApiFilter;
        defaultFilter?: DefaultFilter;
        fields?: FieldsSelection;
        groupBy?: GroupByConfig;
        customParams?: Record<string, string | number>;
    }): HttpParams {
        let params = new HttpParams();

        if (options.limit !== undefined) params = params.append('limit', String(options.limit));
        if (options.page !== undefined) params = params.append('page', String(options.page));
        if (options.orderBy) {
            params = params.append('orderBy',
                typeof options.orderBy === 'string' ? options.orderBy : JSON.stringify(options.orderBy)
            );
        }
        if (options.filter) {
            params = params.append('filter',
                typeof options.filter === 'string' ? options.filter : JSON.stringify(options.filter)
            );
        }
        if (options.defaultFilter) {
            params = params.append('defaultFilter',
                typeof options.defaultFilter === 'string' ? options.defaultFilter : JSON.stringify(options.defaultFilter)
            );
        }
        if (options.fields) {
            params = params.append('fields',
                typeof options.fields === 'string' ? options.fields : JSON.stringify(options.fields)
            );
        }
        if (options.groupBy) {
            params = params.append('groupBy',
                typeof options.groupBy === 'string' ? options.groupBy : JSON.stringify(options.groupBy)
            );
        }

        // Add any custom parameters
        if (options.customParams) {
            Object.entries(options.customParams).forEach(([key, value]) => {
                params = params.append(key, String(value));
            });
        }

        return params;
    }

    /**
     * Gets headers with optional content type
     */
    protected getHeaders(contentType: 'json' | 'form-urlencoded' = 'json'): HttpHeaders {
        if (contentType === 'form-urlencoded') {
            return this.defaultHeaders.set('Content-Type', 'application/x-www-form-urlencoded');
        }
        return this.defaultHeaders;
    }

    /**
     * Generic GET all method
     */
    protected getAll<T = apiResponse>(endpoint: string, options?: {
        limit?: number;
        page?: number;
        orderBy?: string | OrderByConfig[];
        filter?: string | ApiFilter;
        defaultFilter?: DefaultFilter;
        fields?: FieldsSelection;
        groupBy?: GroupByConfig;
        customParams?: Record<string, string | number>;
    }): Observable<T> {
        return this.http.get<T>(`${this.api_base}${endpoint}`, {
            headers: this.defaultHeaders,
            params: this.buildParams(options || {})
        });
    }

    /**
     * Generic GET by ID method
     */
    protected getById<T = apiResponse>(endpoint: string, id: string | number, options?: {
        fields?: FieldsSelection;
        customParams?: Record<string, string | number>;
    }): Observable<T> {
        const url = endpoint.endsWith('/') ? `${endpoint}${id}` : `${endpoint}${id}/`;
        return this.http.get<T>(`${this.api_base}${url}`, {
            headers: this.defaultHeaders,
            params: this.buildParams(options || {})
        });
    }

    /**
     * Generic POST method
     */
    protected post<T = apiResponse>(
        endpoint: string,
        data: any,
        contentType: 'json' | 'form-urlencoded' = 'form-urlencoded'
    ): Observable<T> {
        return this.http.post<T>(`${this.api_base}${endpoint}`, data, {
            headers: this.getHeaders(contentType)
        });
    }

    /**
     * Generic POST with custom request ID
     */
    protected postWithCustomId<T = apiResponse>(
        endpoint: string,
        data: any,
        customRequestId?: string,
        contentType: 'json' | 'form-urlencoded' = 'form-urlencoded'
    ): Observable<T> {
        const params = customRequestId ? new HttpParams().append('customRequestId', customRequestId) : undefined;
        return this.http.post<T>(`${this.api_base}${endpoint}`, data, {
            headers: this.getHeaders(contentType),
            params
        });
    }

    /**
     * Generic PUT method
     */
    protected put<T = apiResponse>(
        endpoint: string,
        id: string | number,
        data: any,
        contentType: 'json' | 'form-urlencoded' = 'form-urlencoded'
    ): Observable<T> {
        const url = endpoint.endsWith('/') ? `${endpoint}${id}` : `${endpoint}${id}/`;
        return this.http.put<T>(`${this.api_base}${url}`, data, {
            headers: this.getHeaders(contentType)
        });
    }

    /**
     * Generic PUT with custom request ID, no path ID
     */
    protected putWithCustomId<T = apiResponse>(
        endpoint: string,
        data: any,
        customRequestId?: string,
        contentType: 'json' | 'form-urlencoded' = 'json'
    ): Observable<T> {
        const params = customRequestId ? new HttpParams().append('customRequestId', customRequestId) : undefined;
        return this.http.put<T>(`${this.api_base}${endpoint}`, data, {
            headers: this.getHeaders(contentType),
            params
        });
    }

    /**
     * Generic PUT without ID
     */
    protected putPath<T = apiResponse>(
        endpoint: string,
        data: any,
        contentType: 'json' | 'form-urlencoded' = 'json'
    ): Observable<T> {
        return this.http.put<T>(`${this.api_base}${endpoint}`, data, {
            headers: this.getHeaders(contentType)
        });
    }

    /**
     * Generic DELETE method
     */
    protected delete<T = apiResponse>(endpoint: string, id: string | number): Observable<T> {
        const url = endpoint.endsWith('/') ? `${endpoint}${id}` : `${endpoint}${id}/`;
        return this.http.delete<T>(`${this.api_base}${url}`, {
            headers: this.defaultHeaders
        });
    }

    /**
     * Generic OPTIONS method
     */
    protected options<T = apiResponse>(endpoint: string): Observable<T> {
        return this.http.options<T>(`${this.api_base}${endpoint}`, {
            headers: this.defaultHeaders
        });
    }
}
